import { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Container, Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where, orderBy, startAt, endAt, doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../Components/Firebase";
import Filters from "../Components/Filters";

const AniSparkPlayer = ({ videoId, episodeId, onPlay, isHovering, onHoverEnd }) => {
  const iframeRef = useRef(null);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [isPreviewEnded, setIsPreviewEnded] = useState(false);
  const [showPreviewIframe, setShowPreviewIframe] = useState(false);

  useEffect(() => {
    if (isHovering && !hasStartedPlaying) {
      setHasStartedPlaying(true);
      setIsPreviewEnded(false);
      setShowPreviewIframe(true);
      
      // Track view when hover preview starts
      if (onPlay) onPlay();
      
      // Stop preview after 12 seconds by removing the iframe
      const previewTimer = setTimeout(() => {
        setIsPreviewEnded(true);
        setShowPreviewIframe(false);
      }, 12000);

      return () => clearTimeout(previewTimer);
    }
    
    // Reset when not hovering
    if (!isHovering) {
      setShowPreviewIframe(false);
      setIsPreviewEnded(false);
      setHasStartedPlaying(false);
    }
  }, [isHovering, hasStartedPlaying, onPlay]);

  return (
    <div 
      style={{ 
        position: 'relative', 
        width: '100%', 
        aspectRatio: '16/9',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#000'
      }}
      onMouseLeave={onHoverEnd}
    >
      {/* Static thumbnail iframe (always present but hidden when preview is active) */}
      <iframe
        title="AniSpark Video Thumbnail"
        src={`https://iframe.videodelivery.net/${videoId}`}
        style={{ 
          width: '100%', 
          height: '100%', 
          border: 'none',
          display: showPreviewIframe ? 'none' : 'block'
        }}
        allow="encrypted-media; picture-in-picture;"
        allowFullScreen
      />
      
      {/* Preview iframe with autoplay (only rendered when hovering) */}
      {showPreviewIframe && (
        <iframe
          ref={iframeRef}
          title="AniSpark Video Preview"
          src={`https://iframe.videodelivery.net/${videoId}?autoplay=true&muted=true`}
          style={{ 
            width: '100%', 
            height: '100%', 
            border: 'none',
            position: 'absolute',
            top: 0,
            left: 0
          }}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
        />
      )}
      
      {/* Overlay for play button or preview ended message */}
      {(!isHovering || isPreviewEnded) && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isPreviewEnded ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
        >
          {isPreviewEnded ? (
            <>
              <div 
                style={{
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  textAlign: 'center',
                  marginBottom: '8px'
                }}
              >
                Preview ended
              </div>
              <div 
                style={{
                  color: '#cbd5e1',
                  fontSize: '13px',
                  textAlign: 'center'
                }}
              >
                Click "Watch Full Episode" to continue
              </div>
            </>
          ) : (
            <div 
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              ▶️
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EpisodeCard = ({ video, onViewTrack }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hoverTimer, setHoverTimer] = useState(null);
  const [hasTrackedView, setHasTrackedView] = useState(false);

  const handleMouseEnter = () => {
    const timer = setTimeout(() => {
      setIsHovering(true);
    }, 500); // Small delay before starting preview
    setHoverTimer(timer);
  };

  const handleMouseLeave = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
    setIsHovering(false);
    setHasTrackedView(false);
  };

  const handlePreviewPlay = () => {
    if (!hasTrackedView) {
      onViewTrack(video.id);
      setHasTrackedView(true);
    }
  };

  const handleHoverEnd = () => {
    setIsHovering(false);
  };

  return (
    <Card 
      className="mb-4 episode-card" 
      style={{ 
        backgroundColor: "#1e1b4b", 
        border: "1px solid #374151",
        borderRadius: "12px",
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <AniSparkPlayer 
        videoId={video.videoId} 
        episodeId={video.id}
        isHovering={isHovering}
        onPlay={handlePreviewPlay}
        onHoverEnd={handleHoverEnd}
      />
      
      <Card.Body className="p-4">
        <div className="mb-3">
          <h5 className="text-light mb-2" style={{ fontSize: '1.1rem', fontWeight: '600', lineHeight: '1.3' }}>
            {video.title}
          </h5>
          
          <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
            <Badge 
              bg="info" 
              className="px-2 py-1"
              style={{ 
                fontSize: '0.8rem',
                fontWeight: '500',
                borderRadius: '16px'
              }}
            >
              {video.genre}
            </Badge>
            {video.founderBadge && (
              <Badge 
                bg="warning" 
                text="dark" 
                className="px-2 py-1"
                style={{ 
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '16px'
                }}
              >
                ✨ Founding Creator
              </Badge>
            )}
            {video.trending && (
              <Badge 
                bg="danger" 
                className="px-2 py-1"
                style={{ 
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                  border: 'none'
                }}
              >
                🔥 Trending
              </Badge>
            )}
          </div>
          
          <div className="d-flex align-items-center gap-3 text-muted" style={{ fontSize: '0.85rem' }}>
            <span className="text-light" style={{ fontWeight: '500' }}>
              {(video.views || 0).toLocaleString()} views
            </span>
            <span>•</span>
            <span>
              {video.date?.toDate().toLocaleDateString()}
            </span>
          </div>
        </div>
        
        <div className="d-flex align-items-center justify-content-between">
          <Link 
            to={`/creator/${video.creatorId}`} 
            className="text-decoration-none"
            style={{ fontSize: '0.9rem' }}
          >
            <Button
              variant="outline-light"
              size="sm"
              className="d-flex align-items-center gap-2"
              style={{
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '0.85rem',
                fontWeight: '500'
              }}
            >
              <span>👤</span>
              View Creator
            </Button>
          </Link>
          
          <Link 
            to={`/watch/${video.id}`}
            style={{ textDecoration: 'none' }}
          >
            <Button
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.3)';
              }}
            >
              Watch Episode
            </Button>
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
};

const Explore = () => {
  const [videos, setVideos] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [isTrending, setIsTrending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // Track video view
  const trackView = async (episodeId) => {
    try {
      const episodeRef = doc(db, "episodes", episodeId);
      await updateDoc(episodeRef, {
        views: increment(1),
        lastViewed: new Date()
      });
      
      // Update local state to reflect the change
      setVideos(prev => prev.map(video => 
        video.id === episodeId 
          ? { ...video, views: (video.views || 0) + 1 }
          : video
      ));
      
      console.log(`View tracked for episode: ${episodeId}`);
    } catch (error) {
      console.error("Error tracking view:", error);
    }
  };

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const episodesRef = collection(db, "episodes");
        let constraints = [];

        if (searchTerm) {
          constraints.push(orderBy("title_lower"));
          constraints.push(startAt(searchTerm.toLowerCase()));
          constraints.push(endAt(searchTerm.toLowerCase() + "\uf8ff"));
          constraints.push(where("nsfw", "==", false));
        } else {
          if (selectedGenre) constraints.push(where("genre", "==", selectedGenre));
          if (isTrending) constraints.push(where("trending", "==", true));
          constraints.push(where("nsfw", "==", false));
        }

        const q = query(episodesRef, ...constraints);
        const querySnapshot = await getDocs(q);
        const results = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setVideos(results);
      } catch (error) {
        console.error("Error fetching videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [selectedGenre, isTrending, searchTerm]);

  if (loading) {
    return (
      <div className="explore-page" style={{ minHeight: "100vh" }}>
        <div className="star-field">
          {Array.from({ length: 100 }).map((_, i) => (
            <div
              key={i}
              className="star"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
        <Container className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
          <div className="text-light text-center">
            <div className="spinner-border text-light mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading episodes...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="explore-page" style={{ minHeight: "100vh" }}>
      <div className="star-field">
        {Array.from({ length: 100 }).map((_, i) => (
          <div
            key={i}
            className="star"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <Container fluid className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            {/* Header Section */}
            <div className="text-center mb-5">
              <h1 className="display-4 fw-bold text-light mb-3" style={{ fontSize: '2.5rem' }}>
                Explore
              </h1>
              <p className="text-light mb-0" style={{ fontSize: '1.1rem', opacity: '0.9' }}>
                Discover amazing short-form anime content from talented creators
              </p>
            </div>

            {/* Filters Section */}
            <div className="mb-5">
              <Card 
                style={{ 
                  backgroundColor: "#1e1b4b", 
                  border: "1px solid #374151",
                  borderRadius: "12px"
                }}
              >
                <Card.Body className="p-4">
                  <Filters
                    onGenreChange={setSelectedGenre}
                    onTrendingToggle={setIsTrending}
                    onReset={() => {
                      setSelectedGenre(null);
                      setIsTrending(false);
                      setSearchTerm("");
                    }}
                    selectedGenre={selectedGenre}
                    isTrending={isTrending}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                  />
                </Card.Body>
              </Card>
            </div>

            {/* Results Section */}
            {videos.length === 0 ? (
              <div className="text-center py-5">
                <div className="text-light">
                  <div style={{ fontSize: '3rem', marginBottom: '20px', opacity: '0.5' }}>🔍</div>
                  <h3 className="mb-3">No episodes found</h3>
                  <p className="text-muted mb-4">
                    Try adjusting your filters or search terms to discover more content.
                  </p>
                  <Button
                    variant="outline-light"
                    onClick={() => {
                      setSelectedGenre(null);
                      setIsTrending(false);
                      setSearchTerm("");
                    }}
                    style={{ borderRadius: '8px' }}
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Results Count */}
                <div className="mb-4">
                  <p className="text-light mb-0" style={{ fontSize: '0.95rem', opacity: '0.8' }}>
                    Found {videos.length} episode{videos.length !== 1 ? 's' : ''}
                    {selectedGenre && ` in ${selectedGenre}`}
                    {isTrending && ` (trending)`}
                    {searchTerm && ` matching "${searchTerm}"`}
                  </p>
                </div>

                {/* Episodes Grid */}
                <Row className="g-4">
                  {videos.map((video) => (
                    <Col key={video.id} lg={4} md={6}>
                      <EpisodeCard 
                        video={video} 
                        onViewTrack={trackView}
                      />
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .episode-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 8px 32px rgba(0,0,0,0.4) !important;
        }
        
        .episode-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .star {
          position: absolute;
          width: 2px;
          height: 2px;
          background: white;
          border-radius: 50%;
          animation: twinkle 3s ease-in-out infinite;
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        
        .explore-page {
          background: linear-gradient(135deg, #1e1b4b 0%, #3c366b 100%);
          position: relative;
          overflow: hidden;
        }
        
        .star-field {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }
        
        .container-fluid {
          position: relative;
          z-index: 2;
        }
      `}</style>
    </div>
  );
};

export default Explore;